import React, { useEffect, useState } from "react";
import { Button, Input, Message, Select } from "semantic-ui-react";
import DataTable from "../dataTable";
import Report from "../../../models/Report";
import User from "../../../models/User";

const earliestDate = "2020-04-01";

const dateValue = (date) =>
    [
        date.getUTCFullYear(),
        String(date.getUTCMonth() + 1).padStart(2, "0"),
        String(date.getUTCDate()).padStart(2, "0"),
    ].join("-");

const currentMonthRange = () => {
    const now = new Date();
    const currentMonth = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1),
    );
    const start = new Date(
        Date.UTC(
            currentMonth.getUTCFullYear(),
            currentMonth.getUTCMonth() - 11,
            1,
        ),
    );
    const end = new Date(
        Date.UTC(
            currentMonth.getUTCFullYear(),
            currentMonth.getUTCMonth() + 1,
            0,
        ),
    );

    return { startDate: dateValue(start), endDate: dateValue(end) };
};

const percentageText = (value) => {
    if (value === null || typeof value === "undefined") {
        return "-";
    }

    return `${Number(Number(value).toFixed(1))}%`;
};

const initialRange = currentMonthRange();

export default function ConciergeTeeTimeBookingOutcomes() {
    const [loading, setLoading] = useState(true);
    const [admins, setAdmins] = useState([]);
    const [data, setData] = useState([]);
    const [error, setError] = useState(false);
    const [filters, setFilters] = useState({
        ...initialRange,
        staffIds: [],
    });

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(false);
            try {
                const response = await User.getMashieAdmins();
                const availableAdmins = response.data.data.sort((left, right) =>
                    left.name.localeCompare(right.name),
                );
                const staffIds = availableAdmins.map((admin) => admin._id);
                setAdmins(availableAdmins);
                setFilters((current) => ({ ...current, staffIds }));
                const reportData = await Report.conciergeTeeTimeBookingOutcomes({
                    startDate: initialRange.startDate,
                    endDate: initialRange.endDate,
                    staffIds: staffIds.join(","),
                });
                setData(reportData);
            } catch (exception) {
                setError(true);
            }
            setLoading(false);
        };

        load();
    }, []);

    const applyFilters = async () => {
        setLoading(true);
        setError(false);
        try {
            const reportData = await Report.conciergeTeeTimeBookingOutcomes({
                startDate: filters.startDate,
                endDate: filters.endDate,
                staffIds: filters.staffIds.join(","),
            });
            setData(reportData);
        } catch (exception) {
            setError(true);
        }
        setLoading(false);
    };

    const showAllTime = () => {
        const nextFilters = {
            ...filters,
            startDate: earliestDate,
            endDate: initialRange.endDate,
        };
        setFilters(nextFilters);
        setLoading(true);
        setError(false);
        Report.conciergeTeeTimeBookingOutcomes({
            startDate: nextFilters.startDate,
            endDate: nextFilters.endDate,
            staffIds: nextFilters.staffIds.join(","),
        })
            .then((reportData) => setData(reportData))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    };

    const headers = [
        { key: "yearMonth", text: "Year / Month" },
        { key: "staff", text: "Staff" },
        { key: "booked", text: "Booked" },
        { key: "cancelled", text: "Cancelled" },
        { key: "bookedPercentage", text: "Booked %" },
        { key: "cancelledPercentage", text: "Cancelled %" },
    ];
    const tableData = data.map((item) => ({
        yearMonth: { value: item.yearMonth, search: item.yearMonth },
        staff: { value: item.staff.name, search: item.staff.name },
        booked: { value: item.booked, search: item.booked },
        cancelled: { value: item.cancelled, search: item.cancelled },
        bookedPercentage: {
            value: percentageText(item.bookedPercentage),
            search: item.bookedPercentage ?? -1,
        },
        cancelledPercentage: {
            value: percentageText(item.cancelledPercentage),
            search: item.cancelledPercentage ?? -1,
        },
    }));
    const maximumDate = initialRange.endDate;

    return (
        <>
            <p>
                Monthly booked and cancelled outcomes attributed to each
                booking&apos;s current claimed staff member.
            </p>

            <div className='row mb-3'>
                <div className='col-sm-12 col-md-3 mb-2'>
                    <label className='with-input'>Start month</label>
                    <Input
                        fluid
                        type='date'
                        min={earliestDate}
                        max={maximumDate}
                        value={filters.startDate}
                        onChange={({ target }) =>
                            setFilters({ ...filters, startDate: target.value })
                        }
                    />
                </div>
                <div className='col-sm-12 col-md-3 mb-2'>
                    <label className='with-input'>End month</label>
                    <Input
                        fluid
                        type='date'
                        min={earliestDate}
                        max={maximumDate}
                        value={filters.endDate}
                        onChange={({ target }) =>
                            setFilters({ ...filters, endDate: target.value })
                        }
                    />
                </div>
                <div className='col-sm-12 col-md-6 mb-2'>
                    <label className='with-input'>Staff</label>
                    <Select
                        fluid
                        search
                        multiple
                        placeholder='Select staff'
                        value={filters.staffIds}
                        options={admins.map((admin) => ({
                            key: admin._id,
                            text: admin.name,
                            value: admin._id,
                        }))}
                        onChange={(_, { value }) =>
                            setFilters({ ...filters, staffIds: value })
                        }
                    />
                </div>
            </div>

            <div className='mb-3'>
                <Button primary className='theme' onClick={applyFilters}>
                    Apply filters
                </Button>
                <Button onClick={showAllTime}>All time</Button>
            </div>

            {error && (
                <Message negative>
                    Failed to load the report. Check the selected dates and try
                    again.
                </Message>
            )}

            <DataTable
                loading={loading}
                headers={headers}
                data={tableData}
                hideSearch
                quickSort={false}
            />
        </>
    );
}
